<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-between align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Report Emails</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                @click="load"
                v-b-tooltip.hover="'Reload'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="text-right">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="saving"
                @click="save"
              >
                <b-spinner v-if="saving" small class="mr-50" />
                <feather-icon v-else icon="SaveIcon" class="mr-50" />
                <span class="align-middle">Save</span>
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-alert variant="primary" show class="mb-1">
            <div class="alert-body" style="font-size: 0.78rem;">
              Each report is emailed once a day, covering the trades that <strong>settle</strong> on that day.
              Every recipient receives a file limited to the accounts they are allowed to see, so a client
              only ever gets their own trades. If nothing settles for a recipient on a given day, they get no email.
            </div>
          </b-alert>

          <b-card
            v-for="report in reports"
            :key="report.ReportType"
            class="report-block mb-1"
            no-body
          >
            <div class="p-1">
              <b-row class="align-items-end">
                <b-col cols="12" md="3" xl="3" lg="3">
                  <b-form-checkbox v-model="report.Enabled" switch class="custom-control-primary mb-50">
                    <strong>{{ report.Label }}</strong>
                  </b-form-checkbox>
                  <small class="text-muted">
                    {{ report.Enabled ? 'Sends daily' : 'Disabled - will not send' }}
                  </small>
                </b-col>

                <b-col cols="12" md="2" xl="2" lg="2">
                  <label class="compact_label">Send Time</label>
                  <b-form-input
                    v-model="report.SendTime"
                    type="time"
                    size="sm"
                    :disabled="!report.Enabled"
                  />
                </b-col>

                <b-col cols="12" md="3" xl="3" lg="3">
                  <label class="compact_label">Timezone</label>
                  <v-select
                    v-model="report.Timezone"
                    :options="timezones"
                    :clearable="false"
                    :disabled="!report.Enabled"
                    placeholder="Timezone"
                  />
                </b-col>

                <b-col cols="12" md="4" xl="4" lg="4">
                  <label class="compact_label">Recipients</label>
                  <v-select
                    class="compact_form_vue-multi-select"
                    v-model="report.selectedUsers"
                    label="label"
                    :options="users"
                    multiple
                    :close-on-select="false"
                    :disabled="!report.Enabled"
                    placeholder="Select user(s)"
                  />
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col cols="12" class="d-flex align-items-end flex-wrap" style="gap: 8px;">
                  <span class="text-muted" style="font-size: 0.75rem;">
                    {{ report.selectedUsers.length }} recipient(s)
                  </span>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    :disabled="report.selectedUsers.length === 0"
                    @click="openTest(report)"
                  >
                    <feather-icon icon="SendIcon" class="mr-50" />
                    <span class="align-middle">Test run...</span>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">Loading...</p>
        </div>
      </template>
    </b-overlay>

    <!-- Send history -->
    <b-card class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">Recent Sends</h5>
        <span class="text-muted" style="font-size: 0.75rem;">Last {{ logs.length }} attempt(s)</span>
      </div>
      <b-table
        class="compact_table report-email-log"
        :items="logs"
        :fields="logColumns"
        :small="true"
        :hover="true"
        :striped="true"
        :bordered="true"
        headVariant="dark"
        responsive
        empty-text="Nothing has been sent yet."
        show-empty
      >
        <template #cell(Status)="data">
          <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
        </template>
        <template #cell(Message)="data">
          <span style="font-size: 0.7rem;">{{ data.value }}</span>
        </template>
      </b-table>
    </b-card>

    <!-- Test run modal -->
    <b-modal
      v-model="testModal"
      :title="'Test run - ' + (testReport ? testReport.Label : '')"
      size="lg"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
    >
      <b-alert variant="warning" show class="mb-1">
        <div class="alert-body" style="font-size: 0.78rem;">
          A test run ignores the send time and the once-a-day guard.
          Leave <strong>Preview only</strong> ticked to see who would receive what
          <strong>without sending anything</strong>. Unticking it sends real email to the
          configured recipients.
        </div>
      </b-alert>

      <b-row class="align-items-end">
        <b-col cols="12" md="5">
          <label class="compact_label">Settlement Date</label>
          <b-form-datepicker
            v-model="testDate"
            :start-weekday="1"
            :hide-header="true"
            class="form-control"
            today-button
            reset-button
          />
        </b-col>
        <b-col cols="12" md="4" style="padding-bottom: 0.4rem;">
          <b-form-checkbox v-model="testDryRun" switch class="custom-control-primary">
            Preview only (no email sent)
          </b-form-checkbox>
        </b-col>
        <b-col cols="12" md="3" style="padding-bottom: 0.4rem;">
          <b-button
            :variant="testDryRun ? 'primary' : 'danger'"
            size="sm"
            block
            :disabled="!testDate || testRunning"
            @click="runTest"
          >
            <b-spinner v-if="testRunning" small class="mr-50" />
            <span class="align-middle">{{ testDryRun ? 'Preview' : 'Send now' }}</span>
          </b-button>
        </b-col>
      </b-row>

      <div v-if="!testDryRun" class="mt-1">
        <b-alert variant="danger" show>
          <div class="alert-body" style="font-size: 0.78rem;">
            This will send real email to
            {{ testReport ? testReport.selectedUsers.length : 0 }} recipient(s).
          </div>
        </b-alert>
      </div>

      <pre v-if="testOutput" class="test-output mt-1">{{ testOutput }}</pre>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormCheckbox, BFormInput, BTable,
  BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormDatepicker, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import reportEmailStoreModule from './reportEmailStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'reportEmails';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormCheckbox, BFormInput, BTable,
    BOverlay, BIcon, BAlert, BSpinner, BBadge, BModal, BFormDatepicker,
    vSelect, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, reportEmailStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      reports: [],
      users: [],
      timezones: [],
      logs: [],
      loading: false,
      saving: false,

      testModal: false,
      testReport: null,
      testDate: null,
      testDryRun: true,
      testRunning: false,
      testOutput: '',
    };
  },

  computed: {
    logColumns() {
      return [
        { key: 'Label', label: 'Report' },
        { key: 'Email', label: 'Recipient' },
        { key: 'SettlementDate', label: 'Settlement Date' },
        { key: 'RowCount', label: 'Rows' },
        { key: 'Status', label: 'Status' },
        { key: 'SentAt', label: 'At' },
        { key: 'Message', label: 'Detail' },
      ];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      store
        .dispatch('reportEmails/getReportEmailSettings')
        .then((res) => {
          const data = res.data || {};
          this.users = data.users || [];
          this.timezones = data.timezones || [];
          this.logs = data.logs || [];

          // hydrate each report's recipient ids into the user objects v-select needs
          this.reports = (data.reports || []).map((report) => ({
            ...report,
            selectedUsers: this.users.filter((u) => (report.userIds || []).includes(u.id)),
          }));

          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.showError(`Error loading report email settings: ${this.errorText(e)}`);
        });
    },

    save() {
      this.saving = true;
      const payload = {
        reports: this.reports.map((report) => ({
          ReportType: report.ReportType,
          Enabled: report.Enabled,
          SendTime: report.SendTime,
          Timezone: report.Timezone,
          userIds: report.selectedUsers.map((u) => u.id),
        })),
      };

      store
        .dispatch('reportEmails/saveReportEmailSettings', payload)
        .then(() => {
          this.saving = false;
          this.showSuccess('Report email settings saved.');
          this.load();
        })
        .catch((e) => {
          this.saving = false;
          this.showError(`Error saving: ${this.errorText(e)}`);
        });
    },

    openTest(report) {
      this.testReport = report;
      this.testDryRun = true;
      this.testOutput = '';
      this.testDate = new Date().toISOString().slice(0, 10);
      this.testModal = true;
    },

    runTest() {
      if (!this.testReport || !this.testDate) return;
      this.testRunning = true;
      this.testOutput = '';

      store
        .dispatch('reportEmails/runReportEmailNow', {
          reportType: this.testReport.ReportType,
          settlementDate: this.testDate,
          dryRun: this.testDryRun,
        })
        .then((res) => {
          this.testRunning = false;
          this.testOutput = (res.data && res.data.output) || '(no output)';
          if (!this.testDryRun) {
            this.showSuccess('Test run sent.');
            this.load();
          }
        })
        .catch((e) => {
          this.testRunning = false;
          this.showError(`Test run failed: ${this.errorText(e)}`);
        });
    },

    statusVariant(status) {
      if (status === 'sent') return 'light-success';
      if (status === 'failed') return 'light-danger';
      return 'light-secondary';
    },

    errorText(e) {
      return (e.response && e.response.data && e.response.data.message) || e.message;
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },

    showSuccess(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Success', text: msg, icon: 'CheckIcon', variant: 'success' },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.report-block {
  border: 1px solid rgba(34, 41, 47, 0.125);
  box-shadow: none;
}

.report-email-log {
  font-size: 0.74rem;
}

.test-output {
  background: rgba(34, 41, 47, 0.05);
  border-radius: 0.3rem;
  padding: 0.6rem;
  font-size: 0.72rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
